import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { costCenterService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { CostCenter as CostCenterType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
import useSetting from "/app/src/components/settings/setting";
import {
  buildExplorerColumns,
  isColumnSelected,
  toggleExplorerColumn,
} from "/app/src/components/explorer/helpers/columns";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import { useAuthState } from "/app/src/contexts/authentication";
/**
 * Renders the Cost Centers explorer component.
 *
 * @returns {JSX.Element} The Cost Centers component.
 */
export default function CostCenters() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();
  const columnHelper = createColumnHelper<CostCenterType>();

  const availableColumns = [
    { id: "description", label: t("translation:description") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "cost_center", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "cost_center",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "name":
          return columnHelper.accessor("name", {
            id: "name",
            header: t("translation:name"),
            cell: (info) => info.getValue(),
          });
        case "description":
          return columnHelper.accessor("description", {
            id: "description",
            header: t("translation:description"),
            cell: (info) => info.getValue(),
          });
        default:
          return null;
      }
    };
    return buildExplorerColumns(
      settingValue,
      "cost_center",
      makeColumn,
      "name",
    );
  }, [settingValue, t, columnHelper]);

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  const [sort, setSort] = useSortUpgrade([]);

  const [searchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const goToCostCenter = useCallback(
    (costCenterId: number) => {
      navigate(`/explorer/cost_centers/${costCenterId}`);
    },
    [navigate],
  );

  const {
    data: costCenters,
    count: costCentersCount,
    isFetching,
  } = usePaginatedData<CostCenterType>({
    queryKey: ["cost_centers"],
    searchString,
    sort,
    page,
    pageSize,
    service: costCenterService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: costCentersCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:cost_centers")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:cost_centers") },
        ]}
      />
      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Table
        loading={isFetching}
        rows={costCenters}
        tableColumns={columns}
        length={costCentersCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToCostCenter}
        emptyText={`${t("translation:no")} ${t("translation:cost_centers")} ${t(
          "translation:found",
        )}`}
      />
    </div>
  );
}
